import { generateStoryApi } from './api/storyApi';
import { auth, db } from '../config/firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { hasResourceAccess } from '../utils/adminUtils';

export async function generateStory(storyInput, wordsPerPage, totalPages, writingStyle) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to generate stories');
    }

    // Create story progress document in Firestore
    const webhooksRef = collection(db, 'storyProgress');
    const webhook = await addDoc(webhooksRef, {
      userId: user.uid,
      status: 'pending',
      storyData: {
        storyInput,
        wordsPerPage,
        totalPages,
        writingStyle
      },
      createdAt: new Date(),
      updatedAt: new Date()
    });

    // Start story generation
    const response = await generateStoryApi(
      storyInput, 
      wordsPerPage, 
      totalPages, 
      writingStyle
    );

    // If API call successful, return webhook ID for polling
    return {
      webhookId: webhook.id,
      status: 'pending'
    };

  } catch (error) {
    console.error('Story generation error:', error);
    // Return fallback content on error
    return {
      content: storyInput,
      status: 'completed',
      isFallback: true
    };
  }
}

export async function checkStoryStatus(webhookId) {
  try {
    console.log('✅ Checking story status for webhookId:', webhookId);
    
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ Authentication error: User not logged in');
      throw new Error('You must be logged in to check story status');
    }

    const webhookRef = doc(db, 'storyProgress', webhookId);
    console.log('📃 Fetching story progress document...');
    const webhook = await getDoc(webhookRef);
    
    if (!webhook.exists()) {
      console.error('❌ Story progress document not found:', webhookId);
      throw new Error('Story progress document not found');
    }

    const data = webhook.data();
    console.log('📊 Webhook data:', JSON.stringify({
      status: data.status,
      hasResult: !!data.result,
      hasContent: !!data.content,
      hasError: !!data.error,
      userId: data.userId ? 'present' : 'missing'
    }));
    
    // Verify ownership or admin access
    const hasAccess = await hasResourceAccess(user, data.userId);
    if (!hasAccess) {
      console.error('❌ Unauthorized access: User ID mismatch and not admin');
      throw new Error('Unauthorized access to story progress document');
    }
    
    // Check if status is completed but result is missing
    if (data.status === 'completed' && !data.result) {
      console.error('⚠️ CRITICAL ISSUE: Story marked as completed but result is missing');
      console.error('⚠️ This is likely causing the UI to get stuck in loading state');
      
      // Force status to failed to break out of loading state
      return {
        status: 'failed',
        content: null,
        error: 'Story generation completed but produced no content. Please try again.'
      };
    }
    
    console.log('✅ Successfully retrieved story status:', data.status);
    return {
      status: data.status,
      content: data.content,
      result: data.result, // Also return result if available
      error: data.error
    };
  } catch (error) {
    console.error('❌ Error checking story status:', error);
    throw error;
  }
}

// Add security rules to allow webhook access
export const webhookSecurityRules = `
service cloud.firestore {
  match /databases/{database}/documents {
    match /webhooks/{webhookId} {
      allow create: if request.auth != null;
      allow read, update: if request.auth != null && request.auth.uid == resource.data.userId;
    }
  }
}
`;